import { MenuItem, TreeNode } from "primeng/api";
import { Template, TemplateSegment, TemplateSegmentTypeId, TemplateTypeId, TemplateWithUsers } from "ng/src/app/templates/shared/template.model";
import { CommentValueValidator, TemplateValueMessage, TemplateValueMessageFn, TemplateValueValidationErrors, TemplateValueValidationTypes, TemplateValueValidator } from "ng/src/app/notes/note-entry/validators/template-value-validator";
import { BehaviorSubject, combineLatest, fromEvent, merge, Observable, of, race, Subject, Subscription } from "rxjs";
import { filter, map, skip, startWith, switchMap, takeUntil } from "rxjs/operators";
import { NoteTemplateValue } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { ChoiceListItem, ChoiceListItemLike } from "ng/src/app/choice-lists/shared/choice-list.model";
import { LogBookUser, LogUser } from "ng/src/app/users/shared/user.model";
import { FileAttachment } from "ng/src/app/notes/shared/file-attachments/file-attachment.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { BaseEntity } from "app/shared/model/app.model";
import { Note } from "ng/src/app/notes/shared/note.model";
import { Utils } from "ng/src/app/core/utils";
import { Nullable, Nullish } from "ng/src/app/shared/model/app.model";
import { Log } from "../../logs/log.model";
import { HasId } from "app/shared/model/properties.model";
import { Calendar } from "primeng/calendar";
import { FormControl } from "@angular/forms";
import { ChoiceListService } from "ng/src/app/choice-lists/shared/choice-list.service";
import { Defer } from "ng/src/app/core/defer";
import FastTemplateValue = NoteTemplateValue.FastTemplateValue;

export interface SegmentTreeNode extends TreeNode {
    parent: SegmentTreeNode | undefined;
    data: (TemplateSegment | { id: number, level: number, name: string } | undefined) & { color?: SectionValidationStatus };
    children: SegmentTreeNode[];
    inputs?: NoteEntryInput[];
}

export interface SegmentTreeNodeMap {
    nodes: SegmentTreeNode[],
    map: { [segmentId: number]: SegmentTreeNode }
}

export class SegmentTreeNode {
    constructor(
        public data: (TemplateSegment | { id: number, level: number, name: string } | undefined) & { color?: SectionValidationStatus },
        private group: NoteEntryGroup,
        private destroy$: Observable<void>
    ) {
        this.label = data?.name;
        this.expanded = true;
        this.children = [];
        this.setUpOnStatusChanges();
    }

    setUpOnStatusChanges() {
        this.group?.controls.forEach(c =>
	        c.onValidation$.pipe(takeUntil(this.destroy$)).subscribe(() => this.onStatusChange())
        );
		this.onStatusChange();
    }

    onStatusChange() {
        this.data.color = SectionValidationStatus.OK;

        for (let noteEntryInput of this.group?.controls ?? []) {
            let errors = noteEntryInput.errors;

            if (errors !== null && (noteEntryInput.commentError || Object.keys(errors).some(e => [
                TemplateValueValidationTypes.REQUIRED.toString(),
                TemplateValueValidationTypes.SIZE_LESS_THAN_MIN.toString(),
                TemplateValueValidationTypes.SIZE_MORE_THAN_MAX.toString(),
            ].indexOf(e) > -1))) {
                this.data.color = SectionValidationStatus.MISSING_DATA;
                return;

            } else if (errors !== null && Object.keys(errors).some(e => [
                TemplateValueValidationTypes.VALUE_LESS_THAN_MIN.toString(),
                TemplateValueValidationTypes.VALUE_MORE_THAN_MAX.toString(),
                TemplateValueValidationTypes.ABNORMAL_VALUE.toString(),
            ].indexOf(e) > -1)) {
                this.data.color = SectionValidationStatus.ABNORMAL;
                // don't return yet, there could be a red control later, and that would take precedence
            }
        }
    }
	
	/**
	 * Expands the node and its parents
	 * @return boolean - Whether any nodes were expanded that were not already expanded
	 */
    expandUp(): boolean {
		const oCollapsed = !this.expanded;
  
		if (oCollapsed)
			this.expanded = true;

        if (this.parent)
            return this.parent.expandUp() || oCollapsed;
		
		return oCollapsed;
    }
}

export interface NoteEntryGroup {
    segmentId: number | undefined,
    templateType: TemplateTypeId
    controls: NoteEntryInput[]
}

export interface LogUserSelection extends LogUser {
	selected: boolean;
}

export interface NotePropertyControls {
	turnover: boolean,
	carryforward: {
		enabled: boolean,
		endDate: Nullish<Date>
	},
	noteTime: Nullish<Date>,
	note: { useRichText: boolean, model: string },
	attachments: FileAttachment[],
	tagValues: TagValue[]
}

export enum SectionValidationStatus {
    OK = 'green',
    ABNORMAL = 'yellow',
    MISSING_DATA = 'red',
}

export enum EditorMode {
	EDITING,
	REVIEWING
}

export enum TocMode {
	NAVIGATOR,
	TOC,
	EDITOR
}

export enum NoteStatusUpdate {
	ACCESS_REVOKED,
	PUBLISHED
}

export enum AutoSyncType {
	AUTO,
	SECTION_CHANGE,
	FORCE
}

export enum SyncType {
	UPDATE_ONLY,
	SYNC,
	PUBLISH
}

export enum CalendarEventAction {
	REVERT,
	FOCUS,
	LEAVE
}

interface EditorCollection {
	tagValues?: number[];
	attachments?: number[];
	fileAttachments?: number[];
	commentAttachments?: number[];
	coCreators?: number[];
}

interface CollectionSupported<T = 'note' | 'value'> {
	addToCollections?: CollectionType<T>,
	removeFromCollections?: CollectionType<T>
}

export type InitModel = {
	template: TemplateWithUsers | null | undefined,
	note: Note.Projections.TemplateValues | null | undefined,
	section: number,
	globalSubstituteLog?: Log | null
}

export type CurrentLogResult = {
	log: Log | null,
	logUsers: LogUser[]
}

type CollectionType<T = 'note' | 'value'> = T extends 'note' ? NoteEditorCollection : ValueEditorCollection;
type NoteEditorCollection = Omit<EditorCollection, 'commentAttachments' | 'fileAttachments'>;
type ValueEditorCollection = Omit<EditorCollection, 'coCreators' | 'attachments'>;
type EditorCollectionType<T = keyof EditorCollection> = (
	T extends 'tagValues' ? TagValue :
	T extends 'coCreators' ? LogUser :
	FileAttachment
)

export interface NoteEntryTemplateValue extends CollectionSupported<'value'>{
	templateSegmentId: number;
	entered: string | null;
	ignoreBaseCollections: true;
	comment: string | null;
	commentEntered: string | null;
	stringValue: string | null;
	dateValue: string | null;
	dateTimeValue: string | null;
	timeValue: string | null;
	userValue: string | null;
	numericValue: number | null;
	richTextValue: string | null;
}

export interface OverflowMenuItem extends MenuItem {
	destroy: () => void
}

export interface NoteEntryStartConfig {
	selectedLog?: number | Log;
	disableLocationModification?: true
}

export interface SegmentSetupOptions {
	introSectionName?: string,
	template?: Template,
	overrideLogId?: number,
	segments?: TemplateSegment[],
	allowedObsoleteSegments?: HasId[]
}

export interface NoteSyncErrorFeedback {
	message: string,
	object: object
}

export type TemplateSegmentValueType<T extends TemplateSegmentTypeId> = Nullable<(
	T extends TemplateSegmentTypeId.TEXT_INPUT ? string :
	T extends TemplateSegmentTypeId.TEXT_CHOICE ? string :
	T extends TemplateSegmentTypeId.NUMERIC_INPUT ? number :
	T extends TemplateSegmentTypeId.CHECKBOX ? boolean :
	T extends TemplateSegmentTypeId.TAG_CHOICE ? Array<TagValue> :
	T extends TemplateSegmentTypeId.DATE_TIME ? Date :
	T extends TemplateSegmentTypeId.FILE_ATTACHMENT ? Array<FileAttachment> :
	T extends TemplateSegmentTypeId.USER_CHOOSER ? LogBookUser :
	string | number | boolean | Array<TagValue | FileAttachment> | Date | LogBookUser
)>

export class ChoiceListItemFetch {
	private readonly listValues: Record<string | number, {
			[value: string]: Array<Defer<ChoiceListItemLike>>
		}> = {}
	
	
	add(listId: string | number, value: string): Promise<ChoiceListItemLike> {
		const defer = new Defer<ChoiceListItemLike>();
		this.getCallbacks(listId, value).push(defer);
		return defer.promise;
	}
	
	execute(service: ChoiceListService): Promise<Record<string, Record<string, ChoiceListItemLike>>> {
		const listSet = new Set<string>();
		const valueSet = new Set<string>();
		
		if (this.isEmpty()) return Promise.resolve({});
		
		Object.keys(this.listValues).forEach(key => {
			const valueEntry = this.listValues[key];
			listSet.add(key);
			Object.keys(valueEntry).forEach(value => valueSet.add(value));
		});
		
		const defer = new Defer<Record<string, Record<string, ChoiceListItemLike>>>();
		
		service.fetchItems({ listId: Array.from(listSet), value: Array.from(valueSet) }).toPromise()
			.then(r => {
				const results = r.reduce((map, value) => {
					if (!map[value.listId])
						map[value.listId] = {};
					
					map[value.listId][value.value] = value;
					return map;
				}, {} as Record<string, Record<string, ChoiceListItemLike>>);
				
				const promises: Array<Promise<ChoiceListItemLike>> = [];
				Object.keys(this.listValues).forEach(listId => {
					Object.keys(this.listValues[listId]).forEach(listValue => {
						const value: ChoiceListItemLike = results?.[listId]?.[listValue] ?? { id: -1 , value: listValue, abnormal: false };
						this.listValues[listId][listValue]?.forEach(defer => {
							defer.resolve(value);
							promises.push(defer.promise);
						});
					});
				});
				defer.resolve(
					Promise.all(promises).then(() => results)
				);
			})
			.catch(error => {
				defer.reject(error);
			})
		
		return defer.promise;
	}
	
	private isEmpty() {
		return Object.keys(this.listValues).length <= 0;
	}
	
	private getCallbacks(listId: string | number, value: string): Array<Defer<ChoiceListItemLike>> {
		if (!this.listValues[listId])
			this.listValues[listId] = {};
			
		if (!this.listValues[listId][value])
			this.listValues[listId][value] = [];
		
		return this.listValues[listId][value];
	}
}


export class NoteEntryTemplateValue {
	
	static create(segment: TemplateSegment | number): NoteEntryTemplateValue {
		return {
			templateSegmentId: (typeof segment === 'number') ? segment : segment.id,
			entered: null,
			ignoreBaseCollections: true,
			comment: null,
			commentEntered: null,
			stringValue: null,
			richTextValue: null,
			dateTimeValue: null,
			dateValue: null,
			timeValue: null,
			userValue: null,
			numericValue: null
		}
	}
	
	static updateCollections(etv: NoteEntryTemplateValue, entity: 'fileAttachments' | 'tagValues' | 'commentAttachments', values: number[], ntv?: NoteTemplateValue) {
		let original: number[] = [];
		let toRemove: number[] = [];
		let toAdd: number[] = Array.from(values);
		
		if (ntv && Array.isArray(ntv[entity])) {
			original = (ntv[entity] as BaseEntity[]).map(e => e.id);
		}
		
		if (original.length) {
			original.forEach(oId => {
				if (!values.some(nId => nId === oId))
					toRemove.push(oId);
				
				let foundIdx = toAdd.findIndex(nId => nId === oId);
				if (foundIdx > -1)
					toAdd.splice(foundIdx, 1);
			})
		}
		
		NoteEntryTemplateValue.getCollection(etv, 'add')[entity] = toAdd;
		NoteEntryTemplateValue.getCollection(etv, 'remove')[entity] = toRemove;
	}
	
	private static getCollection(etv: NoteEntryTemplateValue, type: 'add' | 'remove'): EditorCollection {
		const collectionKey = (type === 'add') ? 'addToCollections' : 'removeFromCollections';
		return etv[collectionKey] = etv[collectionKey] ?? {};
	}
	
	static hasValue(etv: NoteEntryTemplateValue) {
		
		return (hasCollectionUpdate() || etv.entered !== null || etv.commentEntered !== null);
		
		function hasCollectionUpdate() {
			const collections = {
				add: etv.addToCollections ?? {},
				remove: etv.removeFromCollections ?? {}
			};
			return Object.values(collections).some(c =>
				Object.values(c).some(arr => Array.isArray(arr) && arr.length > 0)
			);
		}
	}
	
	static getEditorValueFromValue<T extends TemplateSegmentTypeId = TemplateSegmentTypeId>(ntv: NoteTemplateValue | FastTemplateValue, segment: TemplateSegment<T>): TemplateSegmentValueType<T> {
		switch (segment.type.id) {
			case TemplateSegmentTypeId.TEXT_INPUT:
				return (ntv.richTextValue ?? ntv.stringValue ?? null) as TemplateSegmentValueType<T>;
				
			case TemplateSegmentTypeId.TEXT_CHOICE:
				return (ntv.stringValue ?? null) as TemplateSegmentValueType<T>;
			
			case TemplateSegmentTypeId.NUMERIC_INPUT:
				return( ntv.numericValue ?? null) as TemplateSegmentValueType<T>;
			
			case TemplateSegmentTypeId.CHECKBOX:
				const stringValue = ntv.stringValue;
				return ((stringValue != undefined) ? (stringValue === 'checked') : null) as TemplateSegmentValueType<T>;
			
			case TemplateSegmentTypeId.TAG_CHOICE:
				return (Array.from(ntv.tagValues ?? [])) as TemplateSegmentValueType<T>;
			
			case TemplateSegmentTypeId.DATE_TIME:
				const ref = new Date();
				
				let key: 'dateValue' | 'dateTimeValue' | 'timeValue' = 'dateValue';
				if (segment.includeTime) {
					key = (segment.includeDate) ? 'dateTimeValue' : 'timeValue';
				}
				const value = ntv[key];
				let date = (value == null) ? null : new Date(value);
				
				if (date == null)
					return date;
				
				switch (key) {
					case "timeValue":
						date = ref;
						value?.split(':')?.forEach((str, idx) => {
							const parsed = parseInt(str);
							if (idx === 0)
								date!.setHours(parsed);
							else if (idx === 1)
								date!.setMinutes(parsed);
							else if (idx === 2)
								date!.setSeconds(parsed);
						});
						date.setMilliseconds(0);
						break;
					case "dateValue":
						date = ref;
						value?.split('-')?.forEach((str, idx) => {
							const parsed = parseInt(str);
							if (idx === 0)
								date!.setFullYear(parsed);
							else if (idx === 1)
								date!.setMonth(parsed - 1);
							else if (idx === 2)
								date!.setDate(parsed);
						})
						break;
						
				}
				return date as TemplateSegmentValueType<T>;
			
			case TemplateSegmentTypeId.FILE_ATTACHMENT:
				return Array.from(ntv.fileAttachments ?? []) as TemplateSegmentValueType<T>;
			
			case TemplateSegmentTypeId.USER_CHOOSER:
				return (ntv.userValue ?? null) as TemplateSegmentValueType<T>;
				
			default:
				return null;
		}
	}
	
	static updateEditorValue(editorValue: NoteEntryTemplateValue, segment: TemplateSegment, value: any, useRichText = false, ntv?: NoteTemplateValue) {
		
		switch (segment.type.id) {
			case TemplateSegmentTypeId.TEXT_INPUT:
				const field = useRichText ? 'richTextValue' : 'stringValue';
				const oField = useRichText ? 'stringValue' : 'richTextValue';
				
				editorValue[field] = value;
				editorValue[oField] = null;
				
				break;
			
			case TemplateSegmentTypeId.TEXT_CHOICE:
				editorValue.stringValue = ((Array.isArray(value) ? value[0] : value) as Nullable<ChoiceListItem>)?.value ?? null;
				break;
			
			case TemplateSegmentTypeId.CHECKBOX:
				editorValue.stringValue = (typeof value === 'boolean') ? value ? 'checked' : 'unchecked' : null;
				break;
			
			case TemplateSegmentTypeId.NUMERIC_INPUT:
				editorValue.numericValue = value;
				break;
			
			case TemplateSegmentTypeId.USER_CHOOSER:
				editorValue.userValue = ((Array.isArray(value) ? value[0] : value) as Nullable<LogBookUser>)?._links?.self?.href ?? null;
				break;
			
			case TemplateSegmentTypeId.FILE_ATTACHMENT:
				value = (value as FileAttachment[]).map(fa => fa.id);
				NoteEntryTemplateValue.updateCollections(editorValue, 'fileAttachments', value, ntv);
				break;
			
			case TemplateSegmentTypeId.TAG_CHOICE:
				value = (value as TagValue[]).map(tv => tv.id);
				NoteEntryTemplateValue.updateCollections(editorValue, 'tagValues', value, ntv);
				break;
			
			case TemplateSegmentTypeId.DATE_TIME:
				let key: 'dateValue' | 'timeValue' | 'dateTimeValue' = 'dateValue';
				let settingValue: string = value;
				
				if (segment.includeTime) {
					key = (segment.includeDate) ? 'dateTimeValue' : 'timeValue';
				}
				
				if (value instanceof Date) {
					if (key === 'timeValue') {
						const timeValueDate = new Date();
						timeValueDate.setUTCHours(value.getHours(),
							value.getMinutes(),
							value.getSeconds(),
							value.getMilliseconds());
						
						settingValue = timeValueDate.toISOString();
					}
					else {
						settingValue = value.toISOString();
					}
				}
				
				editorValue[key] = settingValue;
				break;
			
			default:
				return;
		}
	}
}

export interface NoteEntryNote extends CollectionSupported<'note'> {
	draft: boolean;
	noteTime: 'now' | string;
	note: string | null;
	richText: string | null;
	
	log?: string;
	id?: number;
	template?: string | null;
	subTemplate?: string | null;
	turnover: boolean;
	carryForward: boolean;
	carryForwardEndTime: string | null;
	
	newAttachments?: string[];
	removeAttachments?: string[];
	noteTemplateValues?: NoteEntryTemplateValue[];
}

export class NoteEntryNote {
	static new(): NoteEntryNote {
		return {
			draft: true,
			noteTime: 'now',
			carryForward: false,
			carryForwardEndTime: null,
			turnover: false,
			note: null,
			richText: null
		}
	}
	
	static fromTemplate(template: Template): NoteEntryNote {
		return {
			...this.new(),
			template: template._links.self.href,
			draft: template.templateType.id === TemplateTypeId.INSPECTION,
			turnover: template.turnover
		}
	}
	
	static fromNote(note: Note): NoteEntryNote {
		const useRichText = !!note.richText;
		const value: string | null = (useRichText ? note.richText : note.note) ?? null;
		
		return {
			...this.new(),
			note: !useRichText ? value : null,
			richText: useRichText ? value : null,
			noteTime: note.noteTime,
			draft: note.draft,
			turnover: note.turnover,
			carryForward: note.carryForward,
			...note.carryForwardEndTime != null && { carryForwardEndTime: new Date(note.carryForwardEndTime).toISOString() }
		}
	}
	
	static updateCollection<T extends keyof NoteEditorCollection, E extends EditorCollectionType<T>>(note: NoteEntryNote, field: T,
	                                                                                          originalCollection: E[], currentCollection: E[])
	{
		const addToCollection: number[] = [];
		const removeFromCollection: number[] = [];
		const oMap = Utils.arrayToMap(originalCollection, v => v.id);
		const cMap = Utils.arrayToMap(currentCollection, v => v.id);
		
		Object.values(oMap).forEach(oValue => {
			if (!(oValue.id in cMap)) {
				removeFromCollection.push(oValue.id);
			}
		});
		Object.values(cMap).forEach(cValue => {
			if (!(cValue.id in oMap)) {
				addToCollection.push(cValue.id);
			}
		});
		
		note.addToCollections = {
			...note.addToCollections,
			[field]: addToCollection
		}
		note.removeFromCollections = {
			...note.removeFromCollections,
			[field]: removeFromCollection
		}
	}
}

export class CalendarControl {
	private lastGoodValue?: Date;
	private currentValueIsInvalid = false;
	private inputText: string = '';
	private calendarSubject: BehaviorSubject<Calendar>;
	private valueProviderSubscription?: Subscription;
	private valueValidator?: (value: Nullish<Date>) => boolean;
	private transformer?: (value: Nullish<Date>) => Nullish<Date>;
	private onClose?: (d: CalendarControl) => Observable<CalendarEventAction>;
	private onFinalize?: (v: Nullish<Date>) => void;
	private isInteracting = false;
	
	private converterController?: { pattern: RegExp, onMatch: (input:string) => Date | null | 'continue'};
	
	get control() {
		return this._control;
	}
	get isInvalid() {
		return this.currentValueIsInvalid;
	}
	
	constructor(
		calendar: Calendar,
		private _control: FormControl<Nullish<Date>>,
		private unsubscribe: Observable<any>,
		public setToNowOnShow = true,
		public emptyIsValid = true
	) {
		this.calendarSubject = new BehaviorSubject<Calendar>(calendar);
		this.calendarSubject.pipe(takeUntil(unsubscribe)).subscribe(calendar => this.setupCalendarEvents(calendar));
		
		if (_control.value instanceof Date && !isNaN(_control.value.getTime()))
			this.lastGoodValue = _control.value;
	}
	
	updateCalendar(calendar: Calendar) {
		if (calendar !== this.calendarSubject.value)
			this.calendarSubject.next(calendar);
		return this;
	}
	
	setOnCloseHandler(onClose: (delegate: CalendarControl) => Observable<CalendarEventAction>) {
		this.onClose = onClose;
		return this;
	}
	
	setOnFinalizeHandler(onFinalize: (value: Nullish<Date>) => void) {
		this.onFinalize = onFinalize;
		return this;
	}
	
	setValue(value: Date | null | 'now') {
		value = value === 'now' ? new Date() : value;
		this.control.setValue(value);
		return this;
	}
	
	setValueProvider(provider: Observable<Date | null>) {
		this.valueProviderSubscription?.unsubscribe();
		this.valueProviderSubscription = provider.pipe(
			takeUntil(this.unsubscribe)
		).subscribe(value => {
			this.control.setValue(value, { emitEvent: false });
		});
		return this;
	}
	
	setInputTextConverter(options: { pattern: RegExp, onMatch: (v: string) => Date | null | 'continue'}) {
		this.converterController = options;
		return this;
	}
	
	setTransformer(transformer: CalendarControl['transformer']) {
		this.transformer = transformer;
		return this;
	}
	
	setValueValidator(validator: CalendarControl['valueValidator']) {
		this.valueValidator = validator;
		return this;
	}
	
	private setupCalendarEvents(calendar: Calendar) {
		const cancelFunc = takeUntil(race(this.unsubscribe, this.calendarSubject.pipe(skip(1))));
		const getInputText = () => this.getInputElement(calendar).value;
		
		const focusLostMerge$ = merge(
			calendar.onBlur.pipe(
				startWith(null),
				map(event => event?.relatedTarget)
			),
			fromEvent(document, 'focusin', { capture: true }).pipe(
				startWith(null),
				map(() => (document.activeElement as Nullable<HTMLElement>)),
				filter((element) => {
					if (!calendar?.containerViewChild?.nativeElement) return false;
					const isRelated = this.isRelated(calendar.containerViewChild.nativeElement, element);
					if (isRelated) {
						this.isInteracting = true;
						return false;
					}
					else if (this.isInteracting) {
						this.isInteracting = false;
						return true;
					}
					return false;
				})
			)
		).pipe(
			filter((target?: HTMLElement) => {
				if (!target || !calendar?.containerViewChild?.nativeElement) return true;
				return !this.isRelated(calendar.containerViewChild.nativeElement, target);
			})
		);
		
		const onClose$ = calendar.onClose.pipe(startWith(null));
		const beginFinalizing$ = combineLatest([focusLostMerge$, onClose$]).pipe(skip(1));
		
		this.control.valueChanges.pipe(cancelFunc).subscribe((received: any) => {
			let value: Nullish<Date | string> = received;
			if (typeof value === 'string')
				value = null;

			if (this.converterController?.pattern?.test(getInputText())) {
				let newVal = this.converterController!.onMatch(getInputText());
				if (newVal !== 'continue') {
					newVal = newVal && (
							(calendar.maxDate instanceof Date && newVal > calendar.maxDate) ||
							(calendar.minDate instanceof Date && newVal < calendar.minDate)
					)
						? null
						: newVal;
					this.control.setValue(newVal);
					return;
				}
			}
			
			let transformed = this.transformer?.(value);
			if (transformed !== undefined && (transformed != value || (transformed?.getTime() != value?.getTime()))) {
				this.control.setValue(transformed);
			}
			
			let formattedInput: string | null = null;
			if (value instanceof Date) {
				formattedInput = calendar.formatDateTime(value);
			}
			// First timeout to get the updated inputField text from value changes.
			// In some cases the provided value was not in sync with input text field. Usually when selecting from the calendar.
			setTimeout(() => {
				this.inputText = getInputText();
				this.currentValueIsInvalid = (!value && !!this.inputText) || (value && (formattedInput !== this.inputText)) ||
					(!value && !this.emptyIsValid) || (typeof this.valueValidator === 'function' && !this.valueValidator(value as Nullish<Date>));
				
				if (!this.currentValueIsInvalid)
					this.lastGoodValue = value instanceof Date ? value : undefined;
				
				if (calendar.overlayVisible) {
					// Second timeout for allowing Angular to lay out any changes relating to the change of the value being invalid
					// before attempting to realign the overlay.
					setTimeout(() => calendar.overlayVisible && calendar.alignOverlay());
				}
			});
		});
		
		calendar.onShow.pipe(cancelFunc).subscribe(() => {
			if (!this.control.value && !getInputText() && this.setToNowOnShow)
				this.control.setValue(new Date());
			else if (this.inputText && this.inputText !== getInputText()) {
				this.getInputElement(calendar).value = this.inputText;
			}
		});
		
		beginFinalizing$.pipe(
			cancelFunc,
			filter(() => {
				return !calendar.overlayVisible
			}),
			switchMap(() => this.onClose?.(this) ?? of(CalendarEventAction.REVERT))
		).subscribe((action) => {
			switch (action) {
				case CalendarEventAction.LEAVE:
					if (this.currentValueIsInvalid)
						this.control.setValue(null);
					break;
				case CalendarEventAction.FOCUS:
					const el = (calendar.inputfieldViewChild.nativeElement as HTMLInputElement);
					el.focus();
					el.value = this.inputText;
					setTimeout(() => calendar.inputFieldValue = this.inputText);
					break;
				case CalendarEventAction.REVERT:
					if (this.lastGoodValue !== this.control.value)
						this._control.setValue(this.lastGoodValue);
			}
			
			if (action === CalendarEventAction.REVERT || action === CalendarEventAction.LEAVE) {
				this.onFinalize?.(this.control.value);
			}
		});
	}
	
	private isRelated(relative: HTMLElement, child: HTMLElement | null) {
		let node: HTMLElement | null = child;
		while (node) {
			if (node === relative)
				return true;
			
			node = node.parentElement;
		}
		return false;
	}
	
	private getInputElement(calendar: Calendar) {
		return calendar.inputfieldViewChild.nativeElement as HTMLInputElement;
	}
}


export class NoteEntryInput<T = any> {
	
	private _comment: string = '';
	private _commentAttachments: FileAttachment[] = [];
	
	private _entered: Nullable<Date> = null;
	private _commentEntered: Nullable<Date> = null;
	
	private _errorMessage: Nullable<TemplateValueMessage> = null;
	private _commentError = false;
	private errorMessageProvider: Nullable<TemplateValueMessageFn> = null;
	
	private valueValidators: TemplateValueValidator<T>[] = [];
	private commentValidators: CommentValueValidator<T>[] = [];
	
	private _displayingPastValues = false;
	
	errors: Nullable<TemplateValueValidationErrors> = null;
	displayingComments = false;
	
	private displayingPastValuesSubject = new Subject<boolean>();
	readonly displayingPastValues$ = this.displayingPastValuesSubject.asObservable();
	
	private valueSubject = new Subject<T>();
	readonly value$ = this.valueSubject.asObservable();
	
	private commentSubject = new Subject<string>();
	readonly comment$ = this.commentSubject.asObservable();
	
	private commentAttachmentSubject = new Subject<FileAttachment[]>();
	readonly commentAttachment$ = this.commentAttachmentSubject.asObservable();
	
	private validationSubject = new Subject<Nullable<TemplateValueValidationErrors>>();
	readonly validation$ = this.validationSubject.asObservable();
	
	private commentValidationSubject = new Subject<boolean>();
	readonly commentValidation$ = this.commentValidationSubject.asObservable();
	
	readonly onValidation$ = combineLatest([
		this.validationSubject.pipe(startWith(this.errors)),
		this.commentValidationSubject.pipe(startWith(this.commentError))
	]);
	
	readonly onChange$ = combineLatest([
		this.valueSubject.pipe(startWith(null)),
		this.commentSubject.pipe(startWith('')),
		this.commentAttachmentSubject.pipe(startWith([]))
	]);
	
	extras: { [key:string]: any } = {};
	
	get logId() {
		return this._logId;
	}
	
	get templateSegmentId() {
		return this._templateSegment.id;
	}
	
	get templateSegmentType() {
		return this._templateSegment.type.id;
	}
	
	get templateSegment() {
		return this._templateSegment
	}
	
	get value() {
		return this._value;
	}
	set value(val) {
		this.setValue(val);
	}
	
	get errorMessage() {
		return this._errorMessage;
	}
	
	get comment() {
		return this._comment;
	}
	set comment(val) {
		this.setComment(val);
	}
	
	get commentError() {
		return this._commentError;
	}
	
	get commentAttachments() {
		return this._commentAttachments;
	}
	set commentAttachments(val) {
		this.setCommentAttachments(val);
	}
	
	get entered() {
		return this._entered;
	}
	set entered(val) {
		this._entered = val;
	}
	
	get commentEntered() {
		return this._commentEntered;
	}
	set commentEntered(val) {
		this._commentEntered = val;
	}
	
	get displayingPastValues() {
		return this._displayingPastValues;
	}
	set displayingPastValues(val) {
		this.displayingPastValuesSubject.next(this._displayingPastValues = val);
	}
	
	constructor(
		private _logId: number,
		private _templateSegment: TemplateSegment,
		private _value: T,
		entered: Nullable<Date> = null
	) {
		this._entered = entered;
	}
	
	processValueValidators(options?: ValidationOptions) {
		let errors: Nullable<TemplateValueValidationErrors> = null;
		this.valueValidators.forEach(validator => {
			const result = validator(this._value);
			if (result !== null)
				errors = {
					...errors && { ...errors },
					...result
				}
		});
		this.errors = errors;
		
		if (((options?.updateErrorMessage ?? true) === true) || (options?.updateErrorMessage === 'alreadySet' && this.errorMessage)) {
			const allowedTypes = options?.allowedMessageTypes ?? [];
			const message = this.errorMessageProvider?.() ?? null;
			if (!message || allowedTypes.length === 0 || allowedTypes.some(type => message.severity === type)) {
				this._errorMessage = message;
			}
		}
		if (options?.emit !== false) {
			this.validationSubject.next(this.errors);
		}
	}
	
	processCommentValidators(options?: Partial<{ emit: boolean }>) {
		this._commentError = this.commentValidators.some(validator => validator(this.errors, this.comment, this.commentAttachments));
		
		if (options?.emit !== false)
			this.commentValidationSubject.next(this.commentError);
	}
	
	setValue(value: T, options?: SetterOptions & { validationOptions?: ValidationOptions}): NoteEntryInput<T> {
		if (value instanceof Date) {
			value = new Date(value) as any;
			(value as any as Date).setSeconds(0, 0);
		}
		this._value = value;
		
		if (options?.date === undefined)
			this.entered = new Date();
		else if (options?.date === null || options?.date instanceof Date)
			this.entered = options.date;
		
		if (options?.validate !== false)
			this.processValueValidators(options?.validationOptions);
		
		if (options?.emit !== false)
		{
			this.processCommentValidators();
			this.valueSubject.next(this.value);
		}
		
		return this;
	}
	
	setComment(comment: string | CommentUpdate, options?: SetterOptions): NoteEntryInput<T> {
		const emit = options?.emit !== false;
		const update = { comment: emit, attachments: false };
		
		if (typeof comment === 'string') {
			this._comment = comment;
		}
		else {
			if (typeof comment.comment === 'string')
				this._comment = comment.comment;
			if (Array.isArray(comment.attachments)) {
				this._commentAttachments = [...comment.attachments];
				update.attachments = emit;
			}
		}
		
		if (options?.date === undefined)
			this.commentEntered = new Date();
		else if (options?.date === null || options?.date instanceof Date)
			this.commentEntered = options.date;
		
		if (options?.validate !== false)
			this.processCommentValidators();
		
		if (update.comment)
			this.commentSubject.next(this.comment);
		if (update.attachments)
			this.commentAttachmentSubject.next(this.commentAttachments);
		
		return this;
	}
	
	setCommentAttachments(attachments: FileAttachment[], options?: Pick<SetterOptions, 'emit' | 'validate'>): NoteEntryInput<T> {
		this._commentAttachments = [...attachments];
		
		if (options?.validate !== false) {
			this.processCommentValidators();
		}
		
		if (options?.emit !== false) {
			this.commentAttachmentSubject.next(this._commentAttachments);
		}
		
		return this;
	}
	
	setErrorMessageProvider(provider: Nullable<(self: NoteEntryInput<T>) => TemplateValueMessageFn>): NoteEntryInput<T> {
		this.errorMessageProvider = provider?.(this) ?? null;
		return this;
	}
	
	addValueValidators(...validators: TemplateValueValidator<T>[]): NoteEntryInput<T> {
		this.valueValidators.push(...validators);
		return this;
	}
	
	addCommentValidators(...validators: CommentValueValidator<T>[]): NoteEntryInput<T> {
		this.commentValidators.push(...validators);
		return this;
	}
	
	addExtra(key: string, value: any): NoteEntryInput<T> {
		this.extras[key] = value;
		return this;
	}
	
	getExtra(key: string, defaultVal: any = null): Nullable<any> {
		return (key in this.extras) ? this.extras[key] : defaultVal;
	}
	
	clearErrors(type: 'VALUE' | 'COMMENT' | 'ALL') {
		if (type === 'VALUE' || type === 'ALL')
			this.errors = null;
		if (type === 'COMMENT' || type === 'ALL')
			this._commentError = false;
	}
	
	getNoteEditorEntryValue(noteTemplateValue?: NoteTemplateValue): Readonly<NoteEntryTemplateValue> {
		const templateValue = NoteEntryTemplateValue.create(this.templateSegment);
		
		NoteEntryTemplateValue.updateEditorValue(templateValue, this.templateSegment, this.value, !!this.extras['useRichText'], noteTemplateValue);
		NoteEntryTemplateValue.updateCollections(templateValue, 'commentAttachments', this.commentAttachments.map(fa => fa.id), noteTemplateValue);
		
		templateValue.comment = this.comment.trim().length > 0 ? this.comment : null;
		
		templateValue.entered = this.entered?.toISOString() ?? null;
		templateValue.commentEntered = this.commentEntered?.toISOString() ?? null;
		
		Object.freeze(templateValue);
		
		return templateValue;
	}
}

export type ValidationOptions = { updateErrorMessage?: boolean | 'alreadySet', emit?: boolean, allowedMessageTypes?: Array<'info' | 'error' | 'warn'> };
type SetterOptions = { date?: Nullable<Date>, emit?: boolean, validate?: boolean };
type CommentUpdate = { comment?: string, attachments?: FileAttachment[] };